import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getJobsUrl, readJobsApiTarget, writeJobsApiTarget, localJobsFetch } from "../../utils/apiHelper";
import { profileTypeLabels } from "../../utils/categoryLabels";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { Loader2, Check, X } from "lucide-react";

const POLL_MS = 5000;

const SimpleScanProgressModal = ({ isOpen, onClose, jobId, onViewReport }) => {
  const { t } = useTranslation("simpleScanProgressModal");
  const [loading, setLoading] = useState(true);
  const [completionPct, setCompletionPct] = useState(0);
  const [profileName, setProfileName] = useState(null);
  const [profileType, setProfileType] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [lockClose, setLockClose] = useState(false);

  const stopRef = useRef(false);
  const fromLocalRef = useRef(false);
  const fromCloudRef = useRef(false);

  useEffect(() => {
    if (!isOpen || !jobId) {
      stopRef.current = true;
      setLoading(true);
      setCompletionPct(0);
      setProfileName(null);
      setProfileType(null);
      setIsCompleted(false);
      setIsFailed(false);
      setErrorMsg(null);
      setReportId(null);
      setLockClose(false);
      return;
    }

    stopRef.current = false;
    const jobsTarget = readJobsApiTarget();
    fromLocalRef.current = jobsTarget === "localhost";
    fromCloudRef.current = jobsTarget === "cloudserver";
    setLockClose(fromLocalRef.current);
    setLoading(true);
    setCompletionPct(0);
    setProfileName(null);
    setProfileType(null);
    setIsCompleted(false);
    setIsFailed(false);
    setErrorMsg(null);
    setReportId(null);

    let intervalId = null;

    const stopPolling = () => {
      stopRef.current = true;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const finishLocalTarget = () => {
      if (fromLocalRef.current) {
        writeJobsApiTarget("server");
      }
    };

    const poll = async () => {
      if (stopRef.current) return;

      try {
        let response;
        if (fromLocalRef.current) {
          response = await localJobsFetch(`${jobId}/status/`, { method: "GET" });
        } else if (fromCloudRef.current) {
          response = await fetch(getJobsUrl(`${jobId}/status/`, { force: "cloudserver" }), {
            method: "GET",
            credentials: "include",
            redirect: "follow",
          });
        } else {
          response = await fetch(getJobsUrl(`${jobId}/status/`, { force: "server" }), {
            method: "GET",
            credentials: "include",
            redirect: "follow",
          });
        }
        const result = await response.json();
        if (stopRef.current) return;

        const data = result.data || {};
        const overall = data.overall || {};

        if (overall.profile_name) setProfileName(overall.profile_name);
        if (overall.profile_type) setProfileType(overall.profile_type);

        const pct = Number(data.completion_pct);
        setCompletionPct(Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) : 0);
        setLoading(false);

        const status = String(overall.status || "").toLowerCase();

        if (status === "failed") {
          stopPolling();
          setIsFailed(true);
          setErrorMsg(overall.error_msg || null);
          setLockClose(false);
          finishLocalTarget();
          return;
        }

        if (status !== "completed") return;

        stopPolling();
        setIsCompleted(true);
        setLockClose(false);
        setCompletionPct(100);

        const id = data.report_id ?? overall.report_id;
        setReportId(id != null && id !== "" ? id : null);

        finishLocalTarget();
      } catch (error) {
        if (!stopRef.current) {
          console.error("Error fetching job status:", error);
          setLoading(false);
        }
      }
    };

    poll();
    intervalId = setInterval(poll, POLL_MS);

    return stopPolling;
  }, [isOpen, jobId]);

  if (!isOpen) return null;

  const resultEnabled = isCompleted && reportId != null;
  const running = !loading && !isCompleted && !isFailed;
  const title = isFailed
    ? t("titleFailed")
    : isCompleted
      ? t("titleComplete")
      : t("titleInProgress");

  const handleClose = () => {
    if (lockClose) return;
    stopRef.current = true;
    onClose();
  };

  const handleResult = () => {
    if (!resultEnabled) return;
    stopRef.current = true;
    onViewReport?.(reportId);
  };

  const metaLabel = {
    fontSize: "12px",
    color: "var(--muted-text)",
    margin: 0,
  };
  const metaValue = {
    fontSize: "14px",
    color: "var(--card-text)",
    margin: "2px 0 0",
    wordBreak: "break-word",
  };

  return (
    <div
      className="modal-backdrop"
      style={{ zIndex: 9999, padding: "20px" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="modal-content"
        style={{
          width: "100%",
          maxWidth: "420px",
          padding: "28px 28px 24px",
          position: "relative",
          transform: "none",
          top: "auto",
          left: "auto",
          margin: "auto",
          animation: "fadeIn 0.3s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 4px", textAlign: "center" }}>{title}</h3>
        <p
          style={{
            margin: "0 0 24px",
            textAlign: "center",
            fontSize: "13px",
            color: "var(--muted-text)",
          }}
        >
          {t("testId", { jobId })}
        </p>

        {loading ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "40px 0",
              gap: "12px",
            }}
          >
            <Loader2
              size={36}
              style={{
                color: "var(--btn-gradient-start)",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ margin: 0, color: "var(--muted-text)", fontSize: "14px" }}>
              {t("fetchingStatus")}
            </p>
          </div>
        ) : (
          <>
            <div style={{ width: 160, margin: "0 auto 8px" }}>
              <CircularProgressbar
                value={completionPct}
                text={`${completionPct}%`}
                styles={buildStyles({
                  textColor: "var(--card-text)",
                  pathColor: isFailed
                    ? "#dc2626"
                    : isCompleted
                      ? "#00c853"
                      : "var(--btn-gradient-start, #3b82f6)",
                  trailColor: "var(--card-border)",
                  textSize: "18px",
                  pathTransitionDuration: 0.4,
                })}
              />
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                minHeight: "24px",
                marginBottom: "20px",
              }}
            >
              {running && (
                <>
                  <Loader2
                    size={16}
                    style={{
                      color: "var(--btn-gradient-start)",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  <span style={{ fontSize: "13px", color: "var(--muted-text)" }}>
                    {t("testRunning")}
                  </span>
                </>
              )}
              {isCompleted && (
                <>
                  <Check size={16} style={{ color: "#00c853" }} />
                  <span style={{ fontSize: "13px", color: "var(--muted-text)" }}>
                    {t("readyToViewResults")}
                  </span>
                </>
              )}
              {isFailed && (
                <>
                  <X size={16} style={{ color: "#dc2626" }} />
                  <span style={{ fontSize: "13px", color: "var(--muted-text)" }}>
                    {t("testFailed")}
                  </span>
                </>
              )}
            </div>

            {isFailed && errorMsg && (
              <p
                style={{
                  margin: "0 0 12px",
                  textAlign: "center",
                  fontSize: "13px",
                  color: "#dc2626",
                  lineHeight: 1.4,
                  wordBreak: "break-word",
                }}
              >
                {t("error", { message: errorMsg })}
              </p>
            )}

            {(profileName || profileType) && (
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  marginBottom: "24px",
                  paddingTop: "4px",
                }}
              >
                {profileName && (
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={metaLabel}>{t("profile")}</p>
                    <p style={metaValue}>{profileName}</p>
                  </div>
                )}
                {profileType && (
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={metaLabel}>{t("type")}</p>
                    <p style={metaValue}>{profileTypeLabels[profileType] || profileType}</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {lockClose && (
          <p
            style={{
              margin: "0 0 16px",
              fontSize: "12px",
              color: "var(--muted-text)",
              textAlign: "center",
              lineHeight: 1.4,
            }}
          >
            {t("localhostLockHint")}
          </p>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <button
            type="button"
            disabled={!resultEnabled}
            onClick={handleResult}
            style={{
              width: "100%",
              height: "40px",
              padding: "0 16px",
              borderRadius: "8px",
              border: "none",
              fontWeight: 600,
              fontSize: "14px",
              opacity: resultEnabled ? 1 : 0.45,
              cursor: resultEnabled ? "pointer" : "not-allowed",
              background: resultEnabled
                ? "linear-gradient(135deg, var(--btn-gradient-start), var(--btn-gradient-end))"
                : "var(--card-border)",
              color: resultEnabled ? "#fff" : "var(--muted-text)",
            }}
          >
            {t("result")}
          </button>
          <button
            type="button"
            disabled={lockClose}
            onClick={handleClose}
            style={{
              width: "100%",
              height: "40px",
              padding: "0 16px",
              borderRadius: "8px",
              border: "1px solid var(--card-border)",
              fontWeight: 600,
              fontSize: "14px",
              opacity: lockClose ? 0.45 : 1,
              cursor: lockClose ? "not-allowed" : "pointer",
              background: "var(--card-bg)",
              color: "var(--card-text)",
            }}
          >
            {t("close")}
          </button>
        </div>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default SimpleScanProgressModal;
